import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import HasWriteAccess from '@app/src/components/HasWriteAccess'
import type { Provider, ProviderOrganizationResult } from '@app/src/types/organizations'
import {
  DoneAllOutlined,
  ExpandMoreOutlined,
  InfoOutlined,
  LanguageOutlined as LanguageOutlinedIcon,
} from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, List, Stack, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { ConnectionStatusesDrawer } from './ConnectionStatusesDrawer'
import { ProviderListItem } from './ProviderListItem'

interface ProviderSelectionStepProps {
  providers: Provider[]
  selectedProviderIds: number[]
  setSelectedProviderIds: React.Dispatch<React.SetStateAction<number[]>>
  handleConnectSingle: (providerId: number, organizationId: number) => Promise<void>
  handleConnectAll: () => Promise<void>
  isConnectingAll: boolean
  exactMatches: ProviderOrganizationResult[]
  handleViewProvider: (result: ProviderOrganizationResult) => void
}

export const ProviderSelectionStep: React.FC<ProviderSelectionStepProps> = ({
  providers = [],
  selectedProviderIds = [],
  setSelectedProviderIds,
  handleConnectSingle,
  handleConnectAll,
  handleViewProvider,
  isConnectingAll = false,
  exactMatches = [],
}) => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const [connectingProviderId, setConnectingProviderId] = useState<number | null>(null)

  useEffect(() => {
    if (providers?.length && (!selectedProviderIds?.length || selectedProviderIds.length !== providers.length)) {
      setSelectedProviderIds(providers.map(provider => provider.id))
    }
  }, [providers, setSelectedProviderIds])

  const handleConnect = async (providerId: number, organizationId: number) => {
    setConnectingProviderId(providerId)
    try {
      await handleConnectSingle(providerId, organizationId)
      setSelectedProviderIds(prev => prev.filter(pid => pid !== providerId))
    } finally {
      setConnectingProviderId(null)
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <DoneAllOutlined sx={{ color: '#4F8D73', mr: 1 }} />
        <Typography color="text.primary">
          <Box component="span" fontWeight={600}>
            {formatMessage({ id: 'connectOnlyFlowProvidersModal.infoBar.allFieldsMatch' })}
          </Box>{' '}
          {formatMessage({ id: 'connectOnlyFlowProvidersModal.infoBar.fields' })}
        </Typography>
      </Box>
      <Accordion
        elevation={0}
        disableGutters
        defaultExpanded
        sx={{
          bgcolor: 'grey.100',
          '&:before': { position: 'unset' },
          width: '712px',
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreOutlined fontSize="medium" sx={{ mx: 1 }} />} sx={{ py: 1 }}>
          <Stack direction="row" alignItems="center">
            <LanguageOutlinedIcon sx={{ mr: 2, color: 'text.secondary' }} />
            <Typography variant="body1" fontWeight={600}>
              {formatMessage(
                { id: 'connectOnlyFlowProvidersModal.step1.accordionTitle' },
                { count: providers?.length || 0 },
              )}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <List disablePadding>
            {providers.map(provider => (
              <ProviderListItem
                key={provider.id}
                provider={provider}
                isSelected={selectedProviderIds.includes(provider.id)}
                onSelect={() => {
                  setSelectedProviderIds((prev: number[]) =>
                    prev.includes(provider.id)
                      ? prev.filter((pid: number) => pid !== provider.id)
                      : [...prev, provider.id],
                  )
                }}
                onView={() => {
                  const matchingMatch = exactMatches.find(match => match.providerId === provider.id)
                  const result: ProviderOrganizationResult = {
                    providerId: provider.id,
                    organizations: matchingMatch?.organizations || [],
                  }
                  handleViewProvider(result)
                }}
                onConnect={() => {
                  const matchingOrg = exactMatches.find(match => match.providerId === provider.id)
                  const organizationId = matchingOrg?.organizations[0]?.id
                  if (organizationId) {
                    handleConnect(provider.id, organizationId)
                  }
                }}
                isConnecting={connectingProviderId === provider.id}
              />
            ))}
          </List>
        </AccordionDetails>
      </Accordion>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mt: 4 }}>
        <Button
          variant="text"
          color="primary"
          size="large"
          startIcon={<InfoOutlined />}
          onClick={() => openDrawer(<ConnectionStatusesDrawer />, { showAboveDialogs: true })}
        >
          {formatMessage({ id: 'general.learnMore' })}
        </Button>
        <HasWriteAccess>
          <LoadingButton
            variant="contained"
            color="primary"
            size="large"
            onClick={handleConnectAll}
            loading={isConnectingAll}
            disabled={selectedProviderIds.length === 0}
          >
            {formatMessage(
              {
                id:
                  selectedProviderIds.length === 1
                    ? 'connectOnlyFlowProvidersModal.actions.connectOne'
                    : 'connectOnlyFlowProvidersModal.actions.connectAll',
              },
              { count: selectedProviderIds.length },
            )}
          </LoadingButton>
        </HasWriteAccess>
      </Stack>
    </>
  )
}
